import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";

const surfaceSx = {
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: "0 12px 34px rgba(var(--wp-panel-ink-rgb,25,36,47),.055)",
};
const actionSx = { textTransform: "none", fontWeight: 900, borderRadius: 2 };

function money(value, currency = "ARS") {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function moneyRows(rows = []) {
  if (!Array.isArray(rows) || !rows.length) return "—";
  return rows.map((row) => money(row.amount, row.currency)).join(" · ");
}

function quantity(value, unit = "unit") {
  const labels = { unit: "u", pack: "paq", g: "g", kg: "kg", ml: "ml", l: "l", portion: "porc", other: "u" };
  return `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })} ${labels[unit] || unit}`;
}

function daysLabel(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "sin ritmo aún";
  const days = Number(value);
  if (days <= 0.2) return "agotado";
  if (days < 1) return "< 1 día";
  return `${Math.round(days * 10) / 10} días`;
}

function nutritionBasisLabel(row) {
  if (!row) return "";
  if (row.basisUnit === "unit") return `${row.basisQuantity || 1} unidad`;
  return `${row.basisQuantity || 100} ${row.basisUnit || "g"}`;
}

function MetricCard({ icon, label, value, helper }) {
  return (
    <Box sx={{ ...surfaceSx, p: 1.35, minWidth: 0 }}>
      <Stack direction="row" spacing={.7} alignItems="center" sx={{ color: "text.secondary" }}>
        {icon}
        <Typography sx={{ fontSize: 10.7, fontWeight: 1000, textTransform: "uppercase", letterSpacing: ".055em" }}>{label}</Typography>
      </Stack>
      <Typography sx={{ mt: .55, fontWeight: 1000, fontSize: { xs: 19, sm: 23 }, letterSpacing: "-.035em", lineHeight: 1.15, overflowWrap: "anywhere" }}>{value}</Typography>
      {helper ? <Typography sx={{ mt: .45, color: "text.secondary", fontSize: 11.3, lineHeight: 1.4 }}>{helper}</Typography> : null}
    </Box>
  );
}

function MealSyncChip({ meal }) {
  const status = meal?.inventorySync?.status || "not_applicable";
  if (status === "synced") return <Chip size="small" icon={<CheckCircleRoundedIcon />} label="Stock conciliado" variant="outlined" sx={{ fontWeight: 850 }} />;
  if (status === "partial" || status === "needs_review") return <Chip size="small" icon={<WarningAmberRoundedIcon />} label="Stock a revisar" variant="outlined" sx={{ fontWeight: 850 }} />;
  return <Chip size="small" label="Sin descuento explícito" variant="outlined" sx={{ fontWeight: 850 }} />;
}

export default function OwnerFoodPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPeriod = ["week", "month", "30d", "90d"].includes(searchParams.get("period")) ? searchParams.get("period") : "30d";
  const [period, setPeriod] = React.useState(initialPeriod);
  const [horizonDays, setHorizonDays] = React.useState(Number(searchParams.get("horizon")) || 14);
  const [metrics, setMetrics] = React.useState(null);
  const [nutrition, setNutrition] = React.useState(null);
  const [kitchen, setKitchen] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [mealText, setMealText] = React.useState("");
  const [mealSaving, setMealSaving] = React.useState(false);
  const [captureNotice, setCaptureNotice] = React.useState("");
  const [stockIntakeText, setStockIntakeText] = React.useState("");
  const [stockDraft, setStockDraft] = React.useState(null);
  const [stockParsing, setStockParsing] = React.useState(false);
  const [stockSaving, setStockSaving] = React.useState(false);
  const [stockNotice, setStockNotice] = React.useState("");
  const [catalog, setCatalog] = React.useState([]);
  const [budgetAmount, setBudgetAmount] = React.useState("");
  const [budgetSaving, setBudgetSaving] = React.useState(false);
  const autoIntakeRef = React.useRef("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [metricsResponse, nutritionResponse, kitchenResponse, catalogResponse] = await Promise.all([
        ownerApi.foodMetrics({ period, shoppingHorizonDays: horizonDays, planningLookbackDays: 30 }),
        ownerApi.nutrition({ historyDays: 14 }),
        ownerApi.kitchenSnapshot({ limit: 80 }),
        ownerApi.foodCatalog({ limit: 80 }),
      ]);
      setMetrics(metricsResponse?.metrics || null);
      setNutrition(nutritionResponse?.snapshot || null);
      setKitchen(kitchenResponse?.snapshot || null);
      setCatalog(catalogResponse?.foods || []);
      const arsBudget = (metricsResponse?.metrics?.settings?.budgets || []).find((row) => row.currency === "ARS");
      if (arsBudget && !budgetAmount) setBudgetAmount(String(arsBudget.monthlyAmount || ""));
    } catch (err) {
      setError(formatApiError(err, "No pude cargar la realidad de comida."));
    } finally {
      setLoading(false);
    }
  }, [period, horizonDays]);

  React.useEffect(() => { load(); }, [load]);

  const setView = React.useCallback((nextPeriod, nextHorizon = horizonDays) => {
    setPeriod(nextPeriod);
    setHorizonDays(nextHorizon);
    const next = new URLSearchParams(searchParams);
    next.set("period", nextPeriod);
    next.set("horizon", String(nextHorizon));
    setSearchParams(next, { replace: true });
  }, [horizonDays, searchParams, setSearchParams]);

  const submitMeal = async () => {
    const text = mealText.trim();
    if (!text || mealSaving) return;
    setMealSaving(true);
    setError("");
    setCaptureNotice("");
    try {
      const response = await ownerApi.logMeal(text, { inputSource: "manual" });
      const meal = response?.meal;
      const sync = meal?.inventorySync || {};
      const moved = Array.isArray(sync.movements) ? sync.movements.length : 0;
      const issues = Array.isArray(sync.issues) ? sync.issues.length : 0;
      setMealText("");
      setCaptureNotice(moved
        ? `Comida registrada · ${moved} consumo${moved === 1 ? "" : "s"} descontado${moved === 1 ? "" : "s"} del stock real${issues ? ` · ${issues} dato${issues === 1 ? "" : "s"} quedó para revisar` : ""}.`
        : issues
          ? "Comida registrada. No toqué el stock donde la cantidad o el alimento no fueron inequívocos."
          : "Comida registrada. Para descontar stock automáticamente, decime cantidades concretas cuando las sepas." );
      await load();
    } catch (err) {
      setError(formatApiError(err, "No pude registrar esa comida."));
    } finally {
      setMealSaving(false);
    }
  };

  const parseStockIntake = React.useCallback(async (rawText = stockIntakeText) => {
    const text = String(rawText || "").trim();
    if (!text || stockParsing) return;
    setStockParsing(true); setError(""); setStockNotice("");
    try {
      const response = await ownerApi.parseFoodStockIntake(text);
      setStockDraft(response?.draft || null);
      setStockNotice(response?.draft?.summary?.unresolved
        ? `Entendí ${response.draft.summary.ready} items completos y dejé ${response.draft.summary.unresolved} para completar.`
        : `Entendí ${response?.draft?.summary?.ready || 0} items listos para guardar.`);
    } catch (err) {
      setError(formatApiError(err, "No pude separar esa foto de stock."));
    } finally { setStockParsing(false); }
  }, [stockIntakeText, stockParsing]);

  const updateDraftItem = (index, patch) => {
    setStockDraft((current) => current ? { ...current, items: (current.items || []).map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row) } : current);
  };

  const saveStockIntake = async () => {
    if (stockSaving || !stockDraft?.items?.length) return;
    const ready = stockDraft.items.filter((row) => Number(row.quantity) > 0 && (row.unit || row.defaultUnit));
    if (!ready.length) return;
    setStockSaving(true); setError("");
    try {
      const response = await ownerApi.commitFoodStockIntake(ready.map((row) => ({ ...row, unit: row.unit || row.defaultUnit || "unit" })));
      setStockNotice(`Foto guardada · ${response?.saved || ready.length} alimento${(response?.saved || ready.length) === 1 ? "" : "s"} actualizado${(response?.saved || ready.length) === 1 ? "" : "s"}.`);
      setStockDraft(null); setStockIntakeText("");
      await load();
    } catch (err) { setError(formatApiError(err, "No pude guardar la foto de stock.")); }
    finally { setStockSaving(false); }
  };

  const toggleEssential = async (row) => {
    try {
      await ownerApi.updateInventoryItem(row.itemId, { essential: !row.essential });
      await load();
    } catch (err) { setError(formatApiError(err, "No pude actualizar ese alimento base.")); }
  };

  const updateMinimum = async (row, rawValue) => {
    const value = String(rawValue ?? "").trim();
    const minimumQuantity = value === "" ? null : Number(value.replace(",", "."));
    if (minimumQuantity !== null && (!Number.isFinite(minimumQuantity) || minimumQuantity < 0)) return;
    try {
      await ownerApi.updateInventoryItem(row.itemId, { minimumQuantity });
      await load();
    } catch (err) { setError(formatApiError(err, "No pude actualizar el stock mínimo.")); }
  };

  const saveBudget = async () => {
    const amount = Number(String(budgetAmount || "").replace(/[^0-9.,]/g, "").replace(",", "."));
    if (!Number.isFinite(amount) || amount < 0 || budgetSaving) return;
    setBudgetSaving(true); setError("");
    try {
      await ownerApi.updateFoodSettings({ budgets: [{ currency: "ARS", monthlyAmount: amount }] });
      setStockNotice(`Presupuesto mensual de comida actualizado a ${money(amount, "ARS")}.`);
      await load();
    } catch (err) { setError(formatApiError(err, "No pude guardar el presupuesto de comida.")); }
    finally { setBudgetSaving(false); }
  };

  React.useEffect(() => {
    const presetText = String(location.state?.ownerJarvisUi?.preset?.stockIntakeText || "").trim();
    const fromQuery = searchParams.get("stockIntake") === "1";
    if (!presetText || autoIntakeRef.current === presetText) return;
    autoIntakeRef.current = presetText;
    setStockIntakeText(presetText);
    if (fromQuery) window.setTimeout(() => parseStockIntake(presetText), 0);
  }, [location.state, parseStockIntake, searchParams]);

  const planning = metrics?.planning || {};
  const stockRows = planning.stockRows || [];
  const recommendations = planning.recommendations || [];
  const todayMeals = nutrition?.today?.meals || [];
  const coverageIssues = metrics?.dataQuality?.coverageIssues || [];
  const lowStock = metrics?.stock?.low || [];
  const budgetRow = metrics?.budget?.rows?.find((row) => row.currency === "ARS") || null;
  const nutritionPotential = metrics?.nutritionPotential || {};
  const valueEconomics = metrics?.valueEconomics || {};
  const readyStockDraft = stockDraft?.items?.filter((row) => Number(row.quantity) > 0 && (row.unit || row.defaultUnit)) || [];

  return (
    <Box sx={{ maxWidth: 1320, mx: "auto", px: { xs: 1, sm: 1.5 }, py: { xs: 1, sm: 1.5 }, pb: { xs: 9, sm: 2.5 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "flex-start" }}>
        <Box>
          <Stack direction="row" spacing={.7} alignItems="center">
            <RestaurantRoundedIcon />
            <Typography component="h1" sx={{ fontWeight: 1000, fontSize: { xs: 24, sm: 29 }, letterSpacing: "-.04em" }}>Comida</Typography>
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 12.8, maxWidth: 820, lineHeight: 1.5 }}>
            Una sola realidad para lo que comprás, lo que queda, lo que efectivamente consumís y lo que conviene reponer. Jarvis descuenta stock sólo con cantidades defendibles; no inventa consumo para hacer cerrar una métrica.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap">
          <Button variant="outlined" startIcon={<ReceiptLongRoundedIcon />} onClick={() => navigate("/dashboard/owner?jarvisAction=finance", { state: { ownerJarvisUi: { surface: "owner_home", form: "finance", preset: { type: "expense", area: "Personal", category: "Alimentación", trackInventory: "yes" } } } })} sx={actionSx}>Registrar compra</Button>
          <Tooltip title="Actualizar"><IconButton onClick={load} disabled={loading}><RefreshRoundedIcon /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mt: 1.2 }}>{error}</Alert> : null}
      {captureNotice ? <Alert severity="success" sx={{ mt: 1.2 }}>{captureNotice}</Alert> : null}
      {stockNotice ? <Alert severity="info" sx={{ mt: 1.2 }}>{stockNotice}</Alert> : null}

      <Box id="food-stock-intake" sx={{ ...surfaceSx, p: { xs: 1.2, sm: 1.45 }, mt: 1.25, borderColor: searchParams.get("stockIntake") === "1" ? "primary.main" : "divider" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
          <Box>
            <Stack direction="row" spacing={.65} alignItems="center"><AutoAwesomeRoundedIcon fontSize="small" /><Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Foto inicial de stock</Typography></Stack>
            <Typography sx={{ mt: .25, color: "text.secondary", fontSize: 11.7, maxWidth: 860 }}>Dictá todo lo que tenés. Jarvis separa alimentos y cantidades; guardar ajusta sólo lo listado y no crea gastos ni pone en cero lo omitido.</Typography>
          </Box>
          {metrics?.settings?.bootstrapCompletedAt ? <Chip size="small" label="Stock inicializado" variant="outlined" sx={{ fontWeight: 900 }} /> : <Chip size="small" label="Recomendado para empezar" sx={{ fontWeight: 900 }} />}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} sx={{ mt: .9 }} alignItems={{ sm: "flex-start" }}>
          <TextField fullWidth multiline minRows={2} value={stockIntakeText} onChange={(event) => setStockIntakeText(event.target.value)} placeholder="Tengo 12 huevos, 2 kg de pollo, 1 kg de arroz, 2 paquetes de fideos de 500 g, medio litro de aceite..." />
          <Button variant="contained" startIcon={stockParsing ? null : <AutoAwesomeRoundedIcon />} disabled={!stockIntakeText.trim() || stockParsing} onClick={() => parseStockIntake()} sx={{ ...actionSx, minWidth: 160, minHeight: 48 }}>{stockParsing ? <CircularProgress size={20} color="inherit" /> : "Separar stock"}</Button>
        </Stack>
        {stockDraft?.items?.length ? <Box sx={{ mt: 1 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center"><Typography sx={{ fontWeight: 950, fontSize: 12.5 }}>{readyStockDraft.length}/{stockDraft.items.length} listos</Typography><Button size="small" variant="contained" startIcon={<SaveRoundedIcon />} disabled={!readyStockDraft.length || stockSaving} onClick={saveStockIntake} sx={actionSx}>{stockSaving ? "Guardando…" : "Guardar foto"}</Button></Stack>
          <Stack spacing={.45} sx={{ mt: .7 }}>
            {stockDraft.items.map((row, index) => <Box key={row.ref || index} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 92px 88px", md: "minmax(180px,1.3fr) 100px 95px minmax(180px,1fr)" }, gap: .55, p: .65, bgcolor: "action.hover", borderRadius: 1.7, alignItems: "center" }}>
              <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 900, fontSize: 12.1 }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: 9.9 }}>{row.canonicalId ? `${row.category} · catálogo` : "alimento personalizado"}</Typography></Box>
              <TextField size="small" type="number" value={row.quantity ?? ""} onChange={(event) => updateDraftItem(index, { quantity: event.target.value === "" ? null : Number(event.target.value), unit: row.unit || row.defaultUnit || "unit" })} inputProps={{ min: 0, step: "any" }} />
              <TextField select size="small" value={row.unit || row.defaultUnit || "unit"} onChange={(event) => updateDraftItem(index, { unit: event.target.value })}>{[["unit","u"],["pack","paq"],["g","g"],["kg","kg"],["ml","ml"],["l","l"]].map(([value,label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
              <Stack direction="row" spacing={.45} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}><Chip size="small" clickable icon={(row.essential || row.essentialSuggested) ? <StarRoundedIcon /> : <StarBorderRoundedIcon />} label={row.essential ? "Esencial" : row.essentialSuggested ? "Base sugerido" : "Normal"} onClick={() => updateDraftItem(index, { essential: !row.essential })} variant={row.essential ? "filled" : "outlined"} sx={{ fontWeight: 850 }} />{row.nutrition ? <Typography sx={{ color: "text.secondary", fontSize: 9.7 }}>{Math.round(row.nutrition.calories || 0)} kcal · {row.nutrition.proteinGrams || 0} g prot / {nutritionBasisLabel(row.nutrition)}</Typography> : null}</Stack>
            </Box>)}
          </Stack>
          {stockDraft?.issues?.length ? <Alert severity="warning" sx={{ mt: .8, fontSize: 11.2 }}>Hay {stockDraft.issues.length} dato{stockDraft.issues.length === 1 ? "" : "s"} a revisar. Los items sin cantidad concreta no se guardan.</Alert> : null}
        </Box> : null}
      </Box>

      <Box sx={{ ...surfaceSx, p: { xs: 1.2, sm: 1.45 }, mt: 1.0 }}>
        <Typography sx={{ fontWeight: 1000, fontSize: 14.5 }}>Decíselo como pasó</Typography>
        <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.8 }}>Ejemplo: “almorcé 2 huevos, 150 g de arroz y una manzana”. Las cantidades explícitas intentan conciliarse contra stock.</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} sx={{ mt: .9 }} alignItems={{ sm: "flex-start" }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={mealText}
            onChange={(event) => setMealText(event.target.value)}
            placeholder="Cené 250 g de pollo con 200 g de papa..."
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === "Enter") submitMeal();
            }}
          />
          <Button variant="contained" disabled={!mealText.trim() || mealSaving} onClick={submitMeal} sx={{ ...actionSx, minWidth: 150, minHeight: 48 }}>
            {mealSaving ? <CircularProgress size={20} color="inherit" /> : "Registrar comida"}
          </Button>
        </Stack>
      </Box>

      <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: 1.15 }}>
        {[["week", "Semana"], ["month", "Mes"], ["30d", "30 días"], ["90d", "90 días"]].map(([value, label]) => (
          <Chip key={value} clickable onClick={() => setView(value)} label={label} variant={period === value ? "filled" : "outlined"} sx={{ fontWeight: 900 }} />
        ))}
        <Box sx={{ width: 8 }} />
        <TextField select size="small" label="Compra para" value={horizonDays} onChange={(event) => setView(period, Number(event.target.value))} sx={{ minWidth: 142 }}>
          <MenuItem value={7}>7 días</MenuItem>
          <MenuItem value={14}>14 días</MenuItem>
          <MenuItem value={30}>30 días</MenuItem>
        </TextField>
      </Stack>

      {loading && !metrics ? <Box sx={{ py: 8, display: "grid", placeItems: "center" }}><CircularProgress /></Box> : (
        <>
          <Box sx={{ mt: 1.15, display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4,minmax(0,1fr))" }, gap: .8 }}>
            <MetricCard icon={<PaymentsRoundedIcon fontSize="small" />} label="Gasto promedio semanal" value={moneyRows(metrics?.spend?.averagePerWeek)} helper={`Basado en ${metrics?.period?.label || "el período"}.`} />
            <MetricCard icon={<TrendingUpRoundedIcon fontSize="small" />} label="Costo consumido" value={moneyRows(metrics?.consumption?.costEstimate?.totals)} helper="Sólo stock consumido con costo unitario trazable." />
            <MetricCard icon={<Inventory2RoundedIcon fontSize="small" />} label="Stock de comida" value={`${stockRows.filter((row) => Number(row.currentQuantity || 0) > 0).length} items`} helper={lowStock.length ? `${lowStock.length} requieren atención.` : "Sin bajos mínimos detectados."} />
            <MetricCard icon={<ShoppingCartRoundedIcon fontSize="small" />} label={`Compra ${horizonDays} días`} value={moneyRows(planning.estimatedBasket)} helper={`${recommendations.length} reposición${recommendations.length === 1 ? "" : "es"} calculada${recommendations.length === 1 ? "" : "s"}.`} />
          </Box>

          <Box sx={{ mt: .8, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: .8 }}>
            <Box sx={{ ...surfaceSx, p: 1.35 }}>
              <Stack direction="row" spacing={.65} alignItems="center"><AccountBalanceWalletRoundedIcon fontSize="small" /><Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Presupuesto estable</Typography></Stack>
              <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.2 }}>Compara tu presupuesto mensual con el costo observado de sostener el consumo registrado.</Typography>
              <Stack direction="row" spacing={.6} sx={{ mt: .8 }}><TextField size="small" label="Presupuesto mensual ARS" value={budgetAmount} onChange={(event) => setBudgetAmount(event.target.value)} fullWidth /><Button variant="outlined" disabled={budgetSaving || budgetAmount === ""} onClick={saveBudget} sx={actionSx}>{budgetSaving ? "…" : "Guardar"}</Button></Stack>
              {budgetRow ? <Stack spacing={.35} sx={{ mt: .8 }}><Typography sx={{ fontSize: 12 }}><strong>Costo mensual modelado:</strong> {budgetRow.modeledMonthlyCost !== null ? money(budgetRow.modeledMonthlyCost, "ARS") : "todavía sin base"}</Typography><Typography sx={{ fontSize: 12 }}><strong>Margen:</strong> {budgetRow.monthlyGap !== null ? money(budgetRow.monthlyGap, "ARS") : "—"}</Typography><Typography sx={{ fontSize: 12 }}><strong>Duración equivalente del presupuesto al ritmo actual:</strong> {budgetRow.estimatedAffordableDaysAtCurrentRate !== null ? `${budgetRow.estimatedAffordableDaysAtCurrentRate} días` : "sin ritmo suficiente"}</Typography></Stack> : null}
            </Box>
            <Box sx={{ ...surfaceSx, p: 1.35 }}>
              <Stack direction="row" spacing={.65} alignItems="center"><FitnessCenterRoundedIcon fontSize="small" /><Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Potencial nutricional del stock</Typography></Stack>
              <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.2 }}>Referencia aproximada del catálogo, sólo cuando unidad y base nutricional son convertibles.</Typography>
              <Stack direction="row" spacing={1.2} sx={{ mt: .8 }}><Box><Typography sx={{ color: "text.secondary", fontSize: 10 }}>PROTEÍNA EN STOCK</Typography><Typography sx={{ fontWeight: 1000, fontSize: 19 }}>{Number(nutritionPotential?.totals?.proteinGrams || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })} g</Typography></Box><Box><Typography sx={{ color: "text.secondary", fontSize: 10 }}>ENERGÍA REFERENCIAL</Typography><Typography sx={{ fontWeight: 1000, fontSize: 19 }}>{Number(nutritionPotential?.totals?.calories || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })} kcal</Typography></Box></Stack>
              <Typography sx={{ mt: .55, color: "text.secondary", fontSize: 10.7 }}>Cobertura: {nutritionPotential.coveredItems || 0}/{nutritionPotential.totalItems || 0} items con unidad compatible.</Typography>
              {valueEconomics?.bestProteinValue?.length ? <Box sx={{ mt: .75, pt: .7, borderTop: "1px solid", borderColor: "divider" }}><Typography sx={{ color: "text.secondary", fontSize: 9.8, fontWeight: 1000 }}>MEJOR PROTEÍNA / COSTO OBSERVADO</Typography><Stack spacing={.25} sx={{ mt: .35 }}>{valueEconomics.bestProteinValue.slice(0, 3).map((row) => <Stack key={row.itemId} direction="row" justifyContent="space-between" spacing={1}><Typography sx={{ fontSize: 11.2, fontWeight: 850 }}>{row.name}</Typography><Typography sx={{ fontSize: 11.2 }}>{money(row.costPer100gProtein, row.currency)} / 100 g prot.</Typography></Stack>)}</Stack><Typography sx={{ mt: .35, color: "text.secondary", fontSize: 9.6 }}>Referencia económica aproximada; depende del precio observado y la tabla nutricional base.</Typography></Box> : null}
            </Box>
          </Box>

          <Box sx={{ mt: .8, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.05fr) minmax(0,1.55fr)" }, gap: .8 }}>
            <Box sx={{ ...surfaceSx, p: 1.35 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Compra real sugerida</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 11.5 }}>Consumo registrado de {planning.lookbackDays || 30} días + mínimo configurado − stock actual.</Typography>
                </Box>
                <Chip size="small" label={`${horizonDays} días`} variant="outlined" sx={{ fontWeight: 900 }} />
              </Stack>
              <Divider sx={{ my: 1 }} />
              {!recommendations.length ? <Typography sx={{ color: "text.secondary", fontSize: 12.3 }}>Con lo que está registrado, no hace falta reponer nada para este horizonte.</Typography> : (
                <Stack spacing={.7}>
                  {recommendations.slice(0, 14).map((row) => (
                    <Box key={row.itemId} sx={{ p: .8, bgcolor: "action.hover", borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 950, fontSize: 13.2 }}>{row.name}</Typography>
                          <Typography sx={{ mt: .15, color: "text.secondary", fontSize: 10.9 }}>
                            Tenés {quantity(row.currentQuantity, row.unit)} · dura {daysLabel(row.estimatedDaysRemaining)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", flex: "0 0 auto" }}>
                          <Typography sx={{ fontWeight: 1000, fontSize: 13.2 }}>+ {quantity(row.recommendedPurchaseQuantity, row.unit)}</Typography>
                          <Typography sx={{ color: "text.secondary", fontSize: 10.7 }}>{row.recommendedPurchaseCost !== null ? money(row.recommendedPurchaseCost, row.currency) : "sin costo trazable"}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
              {planning.unpriced?.length ? <Alert severity="info" sx={{ mt: 1, fontSize: 11.5 }}>{planning.unpriced.length} alimento{planning.unpriced.length === 1 ? "" : "s"} todavía no tiene{planning.unpriced.length === 1 ? "" : "n"} precio unitario inequívoco. La cantidad recomendada igual se conserva.</Alert> : null}
            </Box>

            <Box sx={{ ...surfaceSx, p: 1.35, overflow: "hidden" }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Stock + velocidad de consumo</Typography>
              <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>No confunde “tener stock” con “haberlo consumido”. La velocidad sale de movimientos de consumo reales.</Typography>
              <Box sx={{ mt: .9, overflowX: "auto" }}>
                <Box sx={{ minWidth: 680 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1.55fr .78fr .78fr .78fr .78fr .9fr", gap: .6, px: .7, pb: .55 }}>
                    {["Alimento", "Stock", "Mínimo", `Consumido ${planning.lookbackDays || 30}d`, "Duración", "Costo/mes"].map((label) => <Typography key={label} sx={{ color: "text.secondary", fontSize: 10.2, fontWeight: 1000, textTransform: "uppercase" }}>{label}</Typography>)}
                  </Box>
                  <Stack spacing={.35}>
                    {stockRows.slice(0, 40).map((row) => (
                      <Box key={row.itemId} sx={{ display: "grid", gridTemplateColumns: "1.55fr .78fr .78fr .78fr .78fr .9fr", gap: .6, alignItems: "center", px: .7, py: .65, borderRadius: 1.6, bgcolor: Number(row.recommendedPurchaseQuantity || 0) > 0 ? "action.hover" : "transparent" }}>
                        <Stack direction="row" spacing={.35} alignItems="center" sx={{ minWidth: 0 }}><Tooltip title={row.essential ? "Esencial: Jarvis lo prioriza al revisar stock" : "Marcar como alimento esencial"}><IconButton size="small" onClick={() => toggleEssential(row)}>{row.essential ? <StarRoundedIcon fontSize="inherit" /> : <StarBorderRoundedIcon fontSize="inherit" />}</IconButton></Tooltip><Typography sx={{ fontWeight: 900, fontSize: 12.2, minWidth: 0 }}>{row.name}</Typography></Stack>
                        <Typography sx={{ fontSize: 11.7 }}>{quantity(row.currentQuantity, row.unit)}</Typography>
                        <TextField size="small" type="number" defaultValue={row.minimumQuantity ?? ""} placeholder="—" onBlur={(event) => updateMinimum(row, event.target.value)} inputProps={{ min: 0, step: "any", "aria-label": `Stock mínimo de ${row.name}` }} sx={{ "& .MuiInputBase-input": { py: .45, px: .7, fontSize: 11.2 } }} />
                        <Typography sx={{ fontSize: 11.7 }}>{quantity(row.consumedInLookback, row.unit)}</Typography>
                        <Typography sx={{ fontSize: 11.7 }}>{daysLabel(row.estimatedDaysRemaining)}</Typography>
                        <Typography sx={{ fontSize: 11.7 }}>{row.monthlyConsumptionCost !== null ? money(row.monthlyConsumptionCost, row.currency) : "—"}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ ...surfaceSx, p: 1.35, mt: .8 }}>
            <Stack direction="row" spacing={.65} alignItems="center"><LocalFireDepartmentRoundedIcon fontSize="small" /><Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Catálogo base de alimentos</Typography></Stack>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.4 }}>Identidades y referencias nutricionales comunes para que “huevos”, “pollo”, “arroz”, “yerba” o “leche” no nazcan como strings aislados. Se amplía sin reemplazar tus datos.</Typography>
            <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" sx={{ mt: .8 }}>{catalog.slice(0, 18).map((food) => <Tooltip key={food.id} title={`${Math.round(food.calories || 0)} kcal · ${food.protein || 0} g proteína por ${nutritionBasisLabel({ basisQuantity: food.basisQuantity, basisUnit: food.basisUnit })}`}><Chip size="small" label={food.name} variant="outlined" sx={{ fontWeight: 800 }} /></Tooltip>)}</Stack>
          </Box>

          <Box sx={{ mt: .8, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: .8 }}>
            <Box sx={{ ...surfaceSx, p: 1.35 }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Comidas de hoy</Typography>
              <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>La comida es el hecho humano; el movimiento de stock es su consecuencia trazable cuando alcanza la información.</Typography>
              {!todayMeals.length ? <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 12.2 }}>Todavía no registraste comidas hoy.</Typography> : (
                <Stack spacing={.65} sx={{ mt: .9 }}>
                  {todayMeals.map((meal) => (
                    <Box key={meal._id || meal.id} sx={{ p: .8, bgcolor: "action.hover", borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                        <Box>
                          <Typography sx={{ fontWeight: 950, fontSize: 12.8 }}>{meal.description || meal.mealType || "Comida"}</Typography>
                          {meal.items?.length ? <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 10.9 }}>{meal.items.map((item) => [item.quantityText, item.name].filter(Boolean).join(" ")).join(" · ")}</Typography> : null}
                        </Box>
                        <MealSyncChip meal={meal} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box sx={{ ...surfaceSx, p: 1.35 }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Calidad de la realidad</Typography>
              <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>Jarvis muestra dónde faltan vínculos en vez de completar números falsos.</Typography>
              <Stack spacing={.55} sx={{ mt: .85 }}>
                <Typography sx={{ fontSize: 12.2 }}><strong>Valor estimado del stock:</strong> {moneyRows(planning.estimatedCurrentStockValue)}</Typography>
                <Typography sx={{ fontSize: 12.2 }}><strong>Consumo mensual proyectado:</strong> {moneyRows(planning.estimatedMonthlyConsumptionCost)}</Typography>
                <Typography sx={{ fontSize: 12.2 }}><strong>Gasto 30 días proyectado:</strong> {moneyRows(metrics?.spend?.projected30Days)}</Typography>
                <Typography sx={{ fontSize: 12.2 }}><strong>Comidas conciliadas con stock:</strong> {metrics?.reconciliation?.mealsWithInventoryConsumption || 0}</Typography>
              </Stack>
              {coverageIssues.length ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 11.8 }}>Brechas que afectan precisión</Typography>
                  <Box component="ul" sx={{ my: .45, pl: 2.1 }}>
                    {coverageIssues.slice(0, 6).map((issue) => <Typography component="li" key={issue} sx={{ fontSize: 11.2, lineHeight: 1.45 }}>{issue}</Typography>)}
                  </Box>
                </Alert>
              ) : <Alert severity="success" sx={{ mt: 1 }}>No detecté huecos evidentes entre los registros disponibles.</Alert>}
              {kitchen?.items?.length ? <Typography sx={{ mt: .85, color: "text.secondary", fontSize: 10.9 }}>Cocina reconoce {kitchen.items.length} alimentos disponibles en este momento.</Typography> : null}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
